import { px } from '@adsk/alloy-react-helpers';

import { AlloyLegacyThemeDesignTokens, TableDesignTokens } from '../types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

export const TABLE_SIZE_TOKENS = {
  SMALL: {
    rowHeight: 36,
    cellPadding: 8,
    iconCellWidth: 44,
  },
  MEDIUM: {
    rowHeight: 48,
    cellPadding: 16,
    iconCellWidth: 52,
  },
  LARGE: {
    rowHeight: 64,
    cellPadding: 16,
    iconCellWidth: 52,
  },
};

export const getTableTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): TableDesignTokens => {
  const { tokens, typography, spacing, halos, borderRadius } = theme;
  const { colors } = tokens;

  const subtlerBorder = colors.border.subtler.value;
  const subtleBorder = colors.border.subtle.value;
  const resizingBorder = colors.border.selected.subtle.value;
  const primaryBg = colors.background.primary.value;
  const hoverBg = colors.background.neutral.hover.value;
  const pressedBg = colors.background.neutral.pressed.value;
  const selectedBg = colors.background.selected.subtler.value;

  return {
    size: TABLE_SIZE_TOKENS,
    cell: {
      _: {
        'border-bottom-width': '1px',
        'border-bottom-style': 'solid',
        'border-bottom-color': subtlerBorder,
        background: primaryBg,
      },
      resizing: {
        'border-right-color': resizingBorder,
      },
    },
    header: {
      _: {
        background: primaryBg,
      },
      primary: {
        'font-weight': typography.bodySmallBold.fontWeight,
        'border-color': subtleBorder,
      },
      secondary: {
        'font-weight': typography.bodySmall.fontWeight,
        'border-color': subtlerBorder,
        'border-right-width': '1px',
        'border-right-style': 'solid',
        'border-right-color': subtlerBorder,
      },
      resizable: {
        '&:hover': {
          background: subtleBorder,
        },
      },
      sortable: {
        _: {
          cursor: 'pointer',
        },
        '&:hover': {
          background: hoverBg,
        },
      },
      resizeIndicator: {
        _: {
          background: 'transparent',
        },
        active: {
          background: resizingBorder,
        },
      },
    },
    row: {
      _: {
        background: primaryBg,
      },
      state: {
        hover: {
          background: hoverBg,
        },
        pressed: {
          background: pressedBg,
        },
        selected: {
          background: selectedBg,
          '&:hover': {
            background: selectedBg,
          },
        },
        disabled: {
          color: colors.text.inactive.value,
        },
      },
      selectable: {
        true: { cursor: 'pointer' },
        false: { cursor: 'default' },
      },
    },
    zebra: {
      odd: {
        background: colors.background.secondary.value,
      },
      hover: {
        background: colors.background.neutral.hover.bold.value,
      },
      pressed: {
        background: pressedBg,
      },
    },
    stickyShadow: {
      left: `${subtlerBorder} 2px 0px 2px`,
      right: `${subtlerBorder} -2px 0px 2px`,
    },
    pagination: {
      _: {
        background: primaryBg,
        'border-top-width': '1px',
        'border-top-style': 'solid',
        'border-top-color': subtleBorder,
      },
      text: {
        color: colors.text.secondary.value,
      },
      button: {
        _: {
          color: colors.icon.primary.value,
        },
        disabled: {
          color: colors.icon.inactive.value,
        },
      },
      navButton: {
        margin: px(4),
      },
      pageInfo: {
        'margin-left': px(spacing.S),
        'margin-right': px(spacing.S),
      },
    },
    loadMore: {
      _: {
        background: primaryBg,
        'border-top-width': '1px',
        'border-top-style': 'solid',
        'border-top-color': subtleBorder,
      },
    },
    skeleton: {
      shimmer: [
        {
          foregroundColor: colors.skeleton.shimmer.five.value,
          backgroundColor: colors.skeleton.shimmer.four.value,
        },
        {
          foregroundColor: colors.skeleton.shimmer.four.value,
          backgroundColor: colors.skeleton.shimmer.three.value,
        },
        {
          foregroundColor: colors.skeleton.shimmer.three.value,
          backgroundColor: colors.skeleton.shimmer.two.value,
        },
        {
          foregroundColor: colors.skeleton.shimmer.two.value,
          backgroundColor: colors.skeleton.shimmer.one.value,
        },
      ],
      'horizontal-padding': px(spacing.L),
    },
    stickyColumns: {
      cell: {
        paddingTop: px(spacing.S),
        paddingBottom: px(spacing.S),
        paddingLeft: px(spacing.XS),
        paddingRight: px(spacing.XS),
      },
      actionsCell: {
        padding: px(spacing.XS),
      },
    },
    configuration: {
      menuHeading: {
        'font-family': typography.heading4.fontFamily,
        'font-weight': typography.heading4.fontWeight,
        'font-size': typography.heading4.fontSize,
        'line-height': typography.heading4.lineHeight,
        color: colors.text.secondary.value,
        'padding-top': '14px',
        'padding-bottom': '14px',
        'padding-left': `${spacing.L}px`,
        'padding-right': `${spacing.L}px`,
      },
      separator: {
        background: subtleBorder,
      },
      option: {
        _: {
          'font-family': typography.bodyMedium.fontFamily,
          'font-weight': typography.bodyMedium.fontWeight,
          'font-size': typography.bodyMedium.fontSize,
          'line-height': typography.bodyMedium.lineHeight,
          display: 'flex',
          'align-items': 'center',
          'padding-top': '10px',
          'padding-bottom': '10px',
          'padding-right': '24px',
        },
        children: {
          true: {
            'padding-left': '8px',
          },
          false: {
            'padding-left': '24px',
          },
        },
        disabled: {
          true: { color: colors.text.inactive.value },
          false: {
            cursor: 'pointer',
            '&:hover': {
              background: hoverBg,
            },
          },
        },
        checkbox: {
          'margin-right': px(spacing.S),
        },
        pinControls: {
          'margin-left': px(spacing.S),
          gap: px(spacing.XS),
        },
      },
      pinButton: {
        _: {
          'border-radius': px(borderRadius.small),
          color: colors.icon.tertiary.value,
        },
        pinned: {
          color: colors.text.primary.value,
        },
        hover: {
          color: colors.icon.hover.value,
        },
      },
      resetButton: {
        _: {
          'margin-right': px(spacing.L),
          'margin-left': px(spacing.L),
          'margin-top': px(18),
          'margin-bottom': px(18),
        },
      },
      densityOption: {
        _: {
          padding: 0,
          'margin-left': px(18),
          'margin-right': 0,
          'margin-top': 0,
          'margin-bottom': 0,
          '&:focus-within': {
            'box-shadow': halos.focus,
          },
        },
        selected: {
          true: {
            color: colors.icon.selected.value,
          },
          false: { color: 'inherit' },
        },
      },
    },
  };
};

export const getTableTokensWeave = (
  weaveTokens: WeaveTokens,
  fontFamily: string,
): TableDesignTokens => {
  const semantic = weaveTokens?.semantic;
  const generic = weaveTokens?.generic;
  const popover = weaveTokens?.component?.popover;
  const skeleton = weaveTokens?.component?.skeleton;
  const table = weaveTokens?.component?.table;
  const cell = table?.cell;
  const header = table?.header;
  const footer = table?.footer;

  const spacing = semantic?.spacing;
  const textLight = semantic?.['text-color']?.light;
  const iconDisabled = semantic?.['icon-color']?.control?.default;

  const tableBg = table?.['background-color'];
  const textColor = table?.['text-color'];
  const cellBorderColor = cell?.['border-color']?.default;
  const cellBorderWidth = cell?.['border-width'];
  const cellHover = cell?.['background-color']?.hover;
  const cellPressed = cell?.['background-color']?.pressed;
  const cellSelected = cell?.['background-color']?.selected?.default;
  const cellSelectedHover = cell?.['background-color']?.selected?.hover;
  const focusHalo = cell?.['box-shadow']?.focus;
  const headerBg = header?.['background-color']?.default;
  const headerHover = header?.['background-color']?.hover;
  const headerWeight = header?.['font-weight']?.selected;
  const stripedBg = table?.striped?.cell?.['background-color'];
  const resizeBorder = table?.resize?.cell?.['border-color'];
  const iconDefault = table?.icon?.fill?.default;
  const iconHoverFill = table?.icon?.fill?.hover;
  const footerBg = footer?.['background-color'];
  const footerBorderColor = footer?.['border-top-color'];
  const footerBorderWidth = footer?.['border-top-width'];
  const pinShadowColor = table?.pinning?.cell?.['box-shadow-color'];
  const skeletonBg = skeleton?.['background-color'];
  const skeletonHighlight = skeleton?.highlight?.['background-color'];

  return {
    size: TABLE_SIZE_TOKENS,
    cell: {
      _: {
        'border-bottom-width': cellBorderWidth,
        'border-bottom-style': 'solid',
        'border-bottom-color': cellBorderColor,
        background: tableBg,
        color: textColor,
      },
      resizing: {
        'border-right-color': resizeBorder,
      },
    },
    header: {
      _: {
        background: headerBg,
        color: textColor,
      },
      primary: {
        'font-weight': headerWeight,
        'border-color': cellBorderColor,
      },
      secondary: {
        'font-weight': 500,
        'border-color': cellBorderColor,
        'border-right-width': cellBorderWidth,
        'border-right-style': 'solid',
        'border-right-color': cellBorderColor,
      },
      resizable: {
        '&:hover': {
          background: headerHover,
        },
      },
      sortable: {
        _: {
          cursor: 'pointer',
        },
        '&:hover': {
          background: headerHover,
        },
      },
      resizeIndicator: {
        _: {
          background: 'transparent',
        },
        active: {
          background: resizeBorder,
        },
      },
    },
    row: {
      _: {
        background: tableBg,
        'font-family': fontFamily,
      },
      state: {
        hover: {
          background: cellHover,
        },
        pressed: {
          background: cellPressed,
        },
        selected: {
          background: cellSelected,
          '&:hover': {
            background: cellSelectedHover ?? cellSelected,
          },
        },
        disabled: {
          color: textLight,
        },
      },
      selectable: {
        true: { cursor: 'pointer' },
        false: { cursor: 'default' },
      },
    },
    zebra: {
      odd: {
        background: stripedBg,
      },
      hover: {
        background: cellHover,
      },
      pressed: {
        background: cellPressed,
      },
    },
    stickyShadow: {
      left:
        pinShadowColor !== undefined
          ? `${pinShadowColor} 2px 0px 2px`
          : undefined,
      right:
        pinShadowColor !== undefined
          ? `${pinShadowColor} -2px 0px 2px`
          : undefined,
    },
    pagination: {
      _: {
        background: footerBg,
        'border-top-width': footerBorderWidth,
        'border-top-style': 'solid',
        'border-top-color': footerBorderColor,
      },
      text: {
        color: textLight,
      },
      button: {
        _: {
          color: iconDefault,
        },
        disabled: {
          color: iconDisabled,
        },
      },
      navButton: {
        margin: spacing?.xxs,
      },
      pageInfo: {
        'margin-left': spacing?.s,
        'margin-right': spacing?.s,
      },
    },
    loadMore: {
      _: {
        background: footerBg,
        'border-top-width': footerBorderWidth,
        'border-top-style': 'solid',
        'border-top-color': footerBorderColor,
      },
    },
    skeleton: {
      shimmer: [
        { foregroundColor: skeletonHighlight, backgroundColor: skeletonBg },
        { foregroundColor: skeletonHighlight, backgroundColor: skeletonBg },
        { foregroundColor: skeletonHighlight, backgroundColor: skeletonBg },
        { foregroundColor: skeletonHighlight, backgroundColor: skeletonBg },
      ],
      'horizontal-padding': spacing?.l,
    },
    stickyColumns: {
      cell: {
        paddingTop: spacing?.s,
        paddingBottom: spacing?.s,
        paddingLeft: spacing?.xs,
        paddingRight: spacing?.xs,
      },
      actionsCell: {
        padding: spacing?.xs,
      },
    },
    configuration: {
      menuHeading: {
        'font-family': fontFamily,
        'font-weight': 700,
        'font-size': generic?.['font-size']?.[14],
        'line-height': generic?.['line-height']?.[20],
        color: textLight,
        'padding-top': '14px',
        'padding-bottom': '14px',
        'padding-left': '24px',
        'padding-right': '24px',
      },
      separator: {
        background: cellBorderColor,
      },
      option: {
        _: {
          'font-family': fontFamily,
          'font-weight': 500,
          'font-size': generic?.['font-size']?.[14],
          'line-height': generic?.['line-height']?.[20],
          display: 'flex',
          'align-items': 'center',
          'padding-top': '10px',
          'padding-bottom': '10px',
          'padding-right': '24px',
        },
        children: {
          true: {
            'padding-left': '8px',
          },
          false: {
            'padding-left': '24px',
          },
        },
        disabled: {
          true: { color: textLight },
          false: {
            cursor: 'pointer',
            '&:hover': {
              background: cellHover,
            },
          },
        },
        checkbox: {
          'margin-right': spacing?.s,
        },
        pinControls: {
          'margin-left': spacing?.s,
          gap: spacing?.xs,
        },
      },
      pinButton: {
        _: {
          'border-radius': popover?.['border-radius'],
          color: iconDefault,
        },
        pinned: {
          color: textColor,
        },
        hover: {
          color: iconHoverFill,
        },
      },
      resetButton: {
        _: {
          'margin-right': spacing?.l,
          'margin-left': spacing?.l,
          'margin-top': px(18),
          'margin-bottom': px(18),
        },
      },
      densityOption: {
        _: {
          padding: 0,
          'margin-left': px(18),
          'margin-right': 0,
          'margin-top': 0,
          'margin-bottom': 0,
          '&:focus-within': {
            'box-shadow': focusHalo,
          },
        },
        selected: {
          true: {
            color: textColor,
          },
          false: {
            color: 'inherit',
          },
        },
      },
    },
  };
};
